// Typed client for the ct-meetings public API. Same-origin in production (the node server
// serves web/dist and /api); the Vite dev server proxies /api → :3030.

/** Topic lifecycle (see server/db/migrations/001_init.sql). */
export type TopicStatus = "registered" | "active" | "resolved" | "closed";

export interface TopicCoverage {
  covered: number;
  total: number;
}

export interface TopicParticipant {
  identity: string;
  name: string | null;
  /** Optional preferred name-color key (see lib/colors.ts); null = auto (hashed). */
  color?: string | null;
}

/** Mirrors `publicTopic()` in server/src/topics/topics.js — never includes the seed. */
export interface Topic {
  id: string;
  slug: string;
  title: string;
  status: TopicStatus;
  position: number;
  summary_md: string | null;
  /** True when summary_md is a seed-derived provisional blurb (not yet from real discussion). */
  summary_is_provisional: boolean;
  resolution_note: string | null;
  ai_suggested_resolution: string | null;
  resolution_confidence: number | null;
  /** AI-suggested question to ask next to move the topic toward resolution (rolling summary). */
  facilitator_question: string | null;
  /** AI-suggested important area not yet discussed (second suggestion category). */
  suggested_next_topic: string | null;
  /** Consolidated top observations (decisions/risks/actions/open questions) from the summary. */
  observations: { type: string; summary: string }[];
  created_at: string;
  updated_at: string;
  coverage: TopicCoverage;
  /** Who is currently in the room(s) discussing this topic. Present for active topics once
   *  the LiveKit-worker → store presence wiring lands; omitted otherwise. */
  participants?: TopicParticipant[];
  /** Output templates attached to this topic (the deliverables it will produce). */
  templates?: { slug: string; name: string }[];
  /** Department this topic belongs to (for dashboard filtering); null = unassigned. */
  department?: string | null;
  /** True when this topic is restricted to its members (see topic_members). */
  is_private?: boolean;
  /** Set only when this topic is an occurrence of a meeting series (see Meeting, below). */
  meeting_id?: string | number | null;
  /** 1-based position of this occurrence within its meeting's series. */
  occurrence_seq?: number | null;
  /** The parent meeting's slug (present alongside meeting_id). */
  meeting_slug?: string | null;
  /** The parent meeting's title (present alongside meeting_id). */
  meeting_title?: string | null;
}

export async function fetchTopics(signal?: AbortSignal): Promise<Topic[]> {
  const res = await fetch("/api/topics", {
    signal,
    headers: { accept: "application/json" },
  });
  if (!res.ok) {
    throw new Error(`Failed to load topics (HTTP ${res.status})`);
  }
  const data = (await res.json()) as { topics?: Topic[] };
  return data.topics ?? [];
}

export async function fetchTopic(
  slugOrId: string,
  signal?: AbortSignal,
): Promise<Topic | null> {
  const res = await fetch(`/api/topics/${encodeURIComponent(slugOrId)}`, {
    signal,
    headers: { accept: "application/json" },
  });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Failed to load topic (HTTP ${res.status})`);
  return (await res.json()) as Topic;
}

export interface WorkProduct {
  id: number;
  topic_id: string;
  template_id: number;
  kind: string;
  title: string;
  content_md?: string;
  version: number;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export async function fetchWorkProducts(
  slugOrId: string,
  signal?: AbortSignal,
): Promise<WorkProduct[]> {
  const res = await fetch(
    `/api/topics/${encodeURIComponent(slugOrId)}/work-products`,
    { signal, headers: { accept: "application/json" } },
  );
  if (res.status === 503) return []; // DB not configured — graceful empty
  if (!res.ok) throw new Error(`Failed to load work products (HTTP ${res.status})`);
  const data = (await res.json()) as { work_products?: WorkProduct[] };
  return data.work_products ?? [];
}

export async function fetchWorkProduct(
  id: number,
  signal?: AbortSignal,
): Promise<WorkProduct | null> {
  const res = await fetch(`/api/work-products/${id}`, {
    signal,
    headers: { accept: "application/json" },
  });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Failed to load work product (HTTP ${res.status})`);
  return (await res.json()) as WorkProduct;
}

export interface RequiredElement {
  key: string;
  label: string;
  question: string;
}

export interface Template {
  id: number;
  slug: string;
  name: string;
  description: string;
  required_elements: RequiredElement[];
}

/** All output templates (for the New-topic picker). */
export async function fetchTemplates(signal?: AbortSignal): Promise<Template[]> {
  const res = await fetch("/api/templates", { signal, headers: { accept: "application/json" } });
  if (res.status === 503) return [];
  if (!res.ok) throw new Error(`Failed to load templates (HTTP ${res.status})`);
  const data = (await res.json()) as { templates?: Template[] };
  return data.templates ?? [];
}

export interface NewTopicResult {
  ok: boolean;
  topic_id: string;
  slug: string;
  title: string;
}

/** Create a PLAIN topic as the signed-in user (no seed — that's MCP/leadership-only). */
export async function createTopic(input: {
  title: string;
  templates?: string[];
  department?: string | null;
  is_private?: boolean;
}): Promise<NewTopicResult> {
  const res = await fetch("/api/topics", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      title: input.title,
      templates: input.templates ?? [],
      department: input.department ?? null,
      is_private: input.is_private ?? false,
    }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `Failed to create topic (HTTP ${res.status})`);
  }
  return (await res.json()) as NewTopicResult;
}

export async function fetchTopicTemplates(
  topicSlug: string,
  signal?: AbortSignal,
): Promise<Template[]> {
  const res = await fetch(
    `/api/topics/${encodeURIComponent(topicSlug)}/templates`,
    { signal, headers: { accept: "application/json" } },
  );
  if (res.status === 503) return [];
  if (!res.ok) throw new Error(`Failed to load templates (HTTP ${res.status})`);
  const data = (await res.json()) as { templates?: Template[] };
  return data.templates ?? [];
}

export interface RenderResult {
  id: number | null;
  template: string;
  title: string;
  content_md: string;
}

export async function renderWorkProduct(
  topicSlug: string,
  templateSlug: string,
  signal?: AbortSignal,
): Promise<RenderResult> {
  const res = await fetch(
    `/api/topics/${encodeURIComponent(topicSlug)}/work-products`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ template: templateSlug, created_by: "web" }),
      signal,
    },
  );
  if (!res.ok) throw new Error(`Failed to render (HTTP ${res.status})`);
  return (await res.json()) as RenderResult;
}

// ── Markdown download URLs ─────────────────────────────────────────────────
// Same-origin GETs, so a plain `<a download href=...>` carries the session cookie and the
// server's Content-Disposition names the file. Each is gated by the same topic access check
// as the view it exports.

/** Download href for a topic's full transcript as markdown. */
export function transcriptMarkdownUrl(topicSlug: string): string {
  return `/api/topics/${encodeURIComponent(topicSlug)}/transcript.md`;
}

/** Download href for a topic's AI meeting notes as markdown. */
export function notesMarkdownUrl(topicSlug: string): string {
  return `/api/topics/${encodeURIComponent(topicSlug)}/notes.md`;
}

/** Download href for a rendered work product (design doc, diagram, template) as markdown. */
export function workProductMarkdownUrl(id: string | number): string {
  return `/api/work-products/${encodeURIComponent(String(id))}/download`;
}

// ── Identity (app-level Google sign-in) ────────────────────────────────────
export interface Me {
  /** True when Google sign-in is configured on the server (creds present). */
  authEnabled: boolean;
  authenticated: boolean;
  email?: string;
  name?: string;
  picture?: string | null;
}

export async function fetchMe(signal?: AbortSignal): Promise<Me> {
  const res = await fetch("/api/me", { signal, headers: { accept: "application/json" } });
  if (!res.ok) throw new Error(`Failed to load identity (HTTP ${res.status})`);
  return (await res.json()) as Me;
}

/** Per-user settings, keyed to your sign-in. */
export interface Prefs {
  greet: boolean;
  /** Preferred display name in rooms ("" = use the CodeTogether account name). */
  name: string;
  /** Preferred name-color key (see lib/colors.ts; "" = auto). */
  color: string;
}

export async function fetchPrefs(signal?: AbortSignal): Promise<Prefs> {
  const res = await fetch("/api/me/prefs", { signal, headers: { accept: "application/json" } });
  if (!res.ok) throw new Error(`Failed to load settings (HTTP ${res.status})`);
  return (await res.json()) as Prefs;
}

export async function updatePrefs(patch: Partial<Prefs>): Promise<Prefs> {
  const res = await fetch("/api/me/prefs", {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`Failed to save settings (HTTP ${res.status})`);
  return (await res.json()) as Prefs;
}

// ── Diagnostics (token + STT cost rollups, in-memory since process start) ──
export interface TokenJobRow {
  job: string;
  tier: string;
  model: string;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
}
export interface TokenDiagnostics {
  since: string;
  totals: { calls: number; inputTokens: number; outputTokens: number; costUsd: number };
  byJob: TokenJobRow[];
}
export async function fetchTokenDiagnostics(signal?: AbortSignal): Promise<TokenDiagnostics> {
  const res = await fetch("/api/diagnostics/tokens", {
    signal,
    headers: { accept: "application/json" },
  });
  if (!res.ok) throw new Error(`Failed to load token diagnostics (HTTP ${res.status})`);
  return (await res.json()) as TokenDiagnostics;
}

export interface SttProviderRow {
  provider: string;
  seconds: number;
  costUsd: number;
  sessions: number;
}
export interface SttRoomRow {
  roomKey: string;
  seconds: number;
  costUsd: number;
  sessions: number;
}
export interface SttDiagnostics {
  since: string;
  pricing: { gladiaUsdPerMin: number };
  totals: { seconds: number; costUsd: number; sessions: number };
  byProvider: SttProviderRow[];
  byRoom: SttRoomRow[];
}
export async function fetchSttDiagnostics(signal?: AbortSignal): Promise<SttDiagnostics> {
  const res = await fetch("/api/diagnostics/stt", {
    signal,
    headers: { accept: "application/json" },
  });
  if (!res.ok) throw new Error(`Failed to load STT diagnostics (HTTP ${res.status})`);
  return (await res.json()) as SttDiagnostics;
}

// ── Ad-hoc Design & Diagram views (Step 9) ─────────────────────────────────
export type DesignViewKind = "design" | "diagram";

/** A pointer to the latest design or diagram work product for a topic. */
export interface DesignViewRef {
  id: string;
  kind: DesignViewKind;
  title: string;
  version: number;
  updated_at: string;
}

export interface DesignViews {
  topic_id: string;
  design: DesignViewRef | null;
  diagram: DesignViewRef | null;
}

export async function fetchDesignViews(
  slugOrId: string,
  signal?: AbortSignal,
): Promise<DesignViews> {
  const res = await fetch(
    `/api/topics/${encodeURIComponent(slugOrId)}/design-views`,
    { signal, headers: { accept: "application/json" } },
  );
  if (res.status === 503 || res.status === 404)
    return { topic_id: slugOrId, design: null, diagram: null };
  if (!res.ok) throw new Error(`Failed to load design views (HTTP ${res.status})`);
  return (await res.json()) as DesignViews;
}

/** Render/refresh one ad-hoc view (Sonnet over the discussion). Returns the new work product. */
export async function refreshDesignView(
  slugOrId: string,
  kind: DesignViewKind,
): Promise<{ id: string; kind: DesignViewKind; title: string; content_md: string }> {
  const res = await fetch(
    `/api/topics/${encodeURIComponent(slugOrId)}/design-views/${kind}`,
    { method: "POST", headers: { "content-type": "application/json" } },
  );
  if (!res.ok) throw new Error(`Failed to refresh ${kind} (HTTP ${res.status})`);
  return (await res.json()) as {
    id: string;
    kind: DesignViewKind;
    title: string;
    content_md: string;
  };
}

// ── Subtopic checklist (participant-facing coverage) ───────────────────────
/** A row in the in-room "Coverage" checklist. Never includes any privileged seed data. */
export interface Subtopic {
  id: string;
  label: string;
  /** 'active' counts toward coverage; 'suggested' is an AI proposal awaiting human approval. */
  state: "active" | "suggested";
  /** Who created it: a participant ('user') or the AI ('ai'). */
  source: "user" | "ai";
  /** AI-owned — humans don't tick boxes. */
  covered: boolean;
  confidence: number;
  evidence: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface SubtopicList {
  topic_id: string;
  active: Subtopic[];
  suggested: Subtopic[];
}

export async function fetchSubtopics(
  slugOrId: string,
  signal?: AbortSignal,
): Promise<SubtopicList> {
  const res = await fetch(
    `/api/topics/${encodeURIComponent(slugOrId)}/subtopics`,
    { signal, headers: { accept: "application/json" } },
  );
  // DB not configured (test mode) or topic not found → an intentional empty checklist.
  if (res.status === 503 || res.status === 404)
    return { topic_id: slugOrId, active: [], suggested: [] };
  if (!res.ok) throw new Error(`Failed to load subtopics (HTTP ${res.status})`);
  return (await res.json()) as SubtopicList;
}

/** Add a participant-authored subtopic to a topic's checklist. */
export async function addSubtopic(
  slugOrId: string,
  label: string,
): Promise<Subtopic> {
  const res = await fetch(
    `/api/topics/${encodeURIComponent(slugOrId)}/subtopics`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ label }),
    },
  );
  if (!res.ok) throw new Error(`Failed to add subtopic (HTTP ${res.status})`);
  return (await res.json()) as Subtopic;
}

/** Remove a subtopic by id. */
export async function deleteSubtopic(
  slugOrId: string,
  subId: string,
): Promise<void> {
  const res = await fetch(
    `/api/topics/${encodeURIComponent(slugOrId)}/subtopics/${subId}`,
    { method: "DELETE" },
  );
  if (!res.ok && res.status !== 404)
    throw new Error(`Failed to remove subtopic (HTTP ${res.status})`);
}

/** Accept an AI-suggested subtopic → moves it into the active checklist. */
export async function acceptSuggestion(
  slugOrId: string,
  subId: string,
): Promise<Subtopic> {
  const res = await fetch(
    `/api/topics/${encodeURIComponent(slugOrId)}/subtopics/${subId}/accept`,
    { method: "POST" },
  );
  if (!res.ok) throw new Error(`Failed to accept suggestion (HTTP ${res.status})`);
  return (await res.json()) as Subtopic;
}

/** Dismiss an AI-suggested subtopic. */
export async function dismissSuggestion(
  slugOrId: string,
  subId: string,
): Promise<void> {
  const res = await fetch(
    `/api/topics/${encodeURIComponent(slugOrId)}/subtopics/${subId}/dismiss`,
    { method: "POST" },
  );
  if (!res.ok && res.status !== 404)
    throw new Error(`Failed to dismiss suggestion (HTTP ${res.status})`);
}

/** Manually check/uncheck a subtopic's coverage (the only path that can un-check one). */
export async function setSubtopicCovered(
  slugOrId: string,
  subId: string,
  covered: boolean,
): Promise<Subtopic> {
  const res = await fetch(
    `/api/topics/${encodeURIComponent(slugOrId)}/subtopics/${subId}/coverage`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ covered }),
    },
  );
  if (!res.ok) throw new Error(`Failed to update coverage (HTTP ${res.status})`);
  return (await res.json()) as Subtopic;
}

// ── Deep-research Max (async research artifacts) ────────────────────────────
export interface ResearchJob {
  id: number;
  question: string;
  status: string; // queued | running | done | failed
  result_md: string | null;
  summary: string | null;
  created_at: string;
}

export async function fetchResearch(slugOrId: string, signal?: AbortSignal): Promise<ResearchJob[]> {
  const res = await fetch(`/api/topics/${encodeURIComponent(slugOrId)}/research`, {
    signal, headers: { accept: "application/json" },
  });
  if (res.status === 503 || res.status === 404) return [];
  if (!res.ok) throw new Error(`Failed to load research (HTTP ${res.status})`);
  return ((await res.json()).research ?? []) as ResearchJob[];
}

/** Ask Max to research a question in the background (runs as the signed-in user). */
export async function postResearch(
  slugOrId: string,
  question: string,
): Promise<{ id?: number; status?: string; deduped?: boolean }> {
  const res = await fetch(`/api/topics/${encodeURIComponent(slugOrId)}/research`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ question }),
  });
  if (!res.ok) throw new Error(`Failed to start research (HTTP ${res.status})`);
  return await res.json();
}

// ── Room / relay (used by the live test room) ──────────────────────────────
export async function fetchRoomToken(roomId: string): Promise<string> {
  const res = await fetch(`/api/rooms/${encodeURIComponent(roomId)}/token`, {
    headers: { accept: "application/json" },
  });
  if (!res.ok) throw new Error(`Failed to get room token (HTTP ${res.status})`);
  const data = (await res.json()) as { token: string };
  return data.token;
}

export async function pointRoom(
  roomId: string,
  topicId: string | null,
): Promise<void> {
  await fetch(`/api/rooms/${encodeURIComponent(roomId)}/point`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ topicId }),
  });
}

export async function resolveTopic(
  topicId: string,
  opts: { resolution_note?: string } = {},
): Promise<Topic> {
  const res = await fetch(`/api/topics/${encodeURIComponent(topicId)}/resolve`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(opts),
  });
  if (!res.ok) throw new Error(`Failed to resolve topic (HTTP ${res.status})`);
  const data = (await res.json()) as { topic: Topic };
  return data.topic;
}

/** Inject a finalized transcript segment (the production-safe test hook). */
export async function injectMockTranscript(
  roomId: string,
  opts: { text: string; speaker?: string; topicId?: string; force?: boolean },
): Promise<void> {
  const res = await fetch(
    `/api/rooms/${encodeURIComponent(roomId)}/mock-transcript`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(opts),
    },
  );
  if (!res.ok) throw new Error(`Inject failed (HTTP ${res.status})`);
}

/** Build the relay WebSocket URL for a token (same-origin; wss under https). */
export function relayWsUrl(token: string): string {
  const proto = location.protocol === "https:" ? "wss" : "ws";
  return `${proto}://${location.host}/ws?token=${encodeURIComponent(token)}`;
}

export interface RoomAudioJoin {
  livekitUrl: string;
  token: string;
  identity: string;
  roomId: string;
}

/** Mint a LiveKit mic-publish token and ensure the recorder bot is subscribed. */
export async function joinRoomAudio(
  roomId: string,
  opts: { identity?: string; name?: string } = {},
): Promise<RoomAudioJoin> {
  const res = await fetch(`/api/rooms/${encodeURIComponent(roomId)}/join`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(opts),
  });
  if (res.status === 503) {
    throw new Error("Live audio isn’t configured on the server.");
  }
  if (!res.ok) throw new Error(`Couldn’t join audio (HTTP ${res.status})`);
  return (await res.json()) as RoomAudioJoin;
}

// ── Membership / privacy (private topics) ──────────────────────────────────
export interface TopicMember {
  email: string;
  role: "owner" | "member";
}

export interface TopicMembers {
  members: TopicMember[];
  /** The signed-in user's role on this topic, or null if not a member. */
  my_role: TopicMember["role"] | null;
  /** Whether the signed-in user may flip this topic's privacy (owner, or the
   * creator of a still-memberless public topic). */
  can_privatize: boolean;
}

export async function fetchTopicMembers(
  slugOrId: string,
  signal?: AbortSignal,
): Promise<TopicMembers> {
  const res = await fetch(
    `/api/topics/${encodeURIComponent(slugOrId)}/members`,
    { signal, headers: { accept: "application/json" } },
  );
  if (!res.ok) throw new Error(`Failed to load members (HTTP ${res.status})`);
  return (await res.json()) as TopicMembers;
}

/** Add (or update the role of) a member — owner only, @codetogether.com only. */
export async function addTopicMember(
  slugOrId: string,
  email: string,
  role?: "owner" | "member",
): Promise<TopicMember> {
  const res = await fetch(
    `/api/topics/${encodeURIComponent(slugOrId)}/members`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ email, role }),
    },
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `Failed to add member (HTTP ${res.status})`);
  }
  return (await res.json()) as TopicMember;
}

/** Remove a member — owner only. */
export async function removeTopicMember(
  slugOrId: string,
  email: string,
): Promise<void> {
  const res = await fetch(
    `/api/topics/${encodeURIComponent(slugOrId)}/members/${encodeURIComponent(email)}`,
    { method: "DELETE" },
  );
  if (!res.ok && res.status !== 404)
    throw new Error(`Failed to remove member (HTTP ${res.status})`);
}

/** Flip a topic's privacy — owner (or, for a public topic, its creator). */
export async function setTopicPrivacy(
  slugOrId: string,
  isPrivate: boolean,
): Promise<{ is_private: boolean }> {
  const res = await fetch(
    `/api/topics/${encodeURIComponent(slugOrId)}/privacy`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ is_private: isPrivate }),
    },
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `Failed to update privacy (HTTP ${res.status})`);
  }
  return (await res.json()) as { is_private: boolean };
}

// ── Meetings (series) + occurrences (Phase B) ──────────────────────────────
// A Meeting is standing config for a recurring series; "Start this week's <title>" stamps out
// a fresh occurrence Topic (see the meeting_id/occurrence_seq fields added to Topic, above).

/** A recurrence rule (see `server/src/meetings/recurrence.js`, Phase C). */
export interface Recurrence {
  freq: "weekly" | "monthly";
  /** Every N weeks/months, >= 1. */
  interval: number;
  /** Required for `freq:'weekly'` — 0=Sun..6=Sat. */
  byweekday?: number[];
  /** Required for `freq:'monthly'` — 1..31 (clamped to month length server-side). */
  bymonthday?: number;
  /** 24h wall-clock, e.g. "09:00". */
  time: string;
  /** IANA zone, e.g. "America/Los_Angeles". */
  tz: string;
}

/** Mirrors `publicMeeting()` in server/src/meetings/meetings.js — never includes a seed. */
export interface Meeting {
  id: string | number;
  slug: string;
  title: string;
  owner_email: string | null;
  is_private: boolean;
  agenda: string[];
  templates: string[];
  created_at: string;
  updated_at: string;
  /** The signed-in user's role on this meeting ("owner" | "member"), present on list/detail. */
  my_role?: "owner" | "member" | null;
  /** How many occurrences this meeting has had so far. */
  occurrence_count?: number;
  /** When the most recent occurrence was created, if any. */
  last_occurrence_at?: string | null;
  /** The meeting's schedule rule, if any (Phase C). Null/absent = unscheduled. */
  recurrence?: Recurrence | null;
  /** The next instant an occurrence is due (UTC), if `auto_create` is armed. */
  next_run_at?: string | null;
  /** Master switch — the scheduler ignores meetings where this is false. */
  auto_create?: boolean;
}

/** A past/current occurrence, as summarized in `GET /api/meetings/:id`. */
export interface Occurrence {
  id: string;
  slug: string;
  title: string;
  occurrence_seq: number;
  status: TopicStatus;
  created_at: string;
}

export interface MeetingMember {
  email: string;
  role: "owner" | "member";
}

export interface MeetingDetail {
  meeting: Meeting;
  members: MeetingMember[];
  my_role: MeetingMember["role"] | null;
  can_privatize: boolean;
  occurrences: Occurrence[];
}

/** Thrown by `createMeeting` on a non-OK response; carries the HTTP status so callers can
 *  special-case e.g. 409 ("already exists") without string-matching the message. */
export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

/** Meetings the signed-in user is a member (or owner) of. */
export async function fetchMeetings(signal?: AbortSignal): Promise<Meeting[]> {
  const res = await fetch("/api/meetings", { signal, headers: { accept: "application/json" } });
  if (!res.ok) throw new Error(`Failed to load meetings (HTTP ${res.status})`);
  const data = (await res.json()) as { meetings?: Meeting[] };
  return data.meetings ?? [];
}

/** A single meeting's detail: roster, agenda/templates, and its past occurrences. */
export async function fetchMeeting(
  idOrSlug: string | number,
  signal?: AbortSignal,
): Promise<MeetingDetail> {
  const res = await fetch(`/api/meetings/${encodeURIComponent(String(idOrSlug))}`, {
    signal,
    headers: { accept: "application/json" },
  });
  if (!res.ok) throw new Error(`Failed to load meeting (HTTP ${res.status})`);
  return (await res.json()) as MeetingDetail;
}

/** Create a meeting series as the signed-in user. Creator becomes owner. */
export async function createMeeting(input: {
  title: string;
  is_private?: boolean;
  agenda?: string[];
  templates?: string[];
}): Promise<{ ok: boolean; meeting_id: string; slug: string; title: string }> {
  const res = await fetch("/api/meetings", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      title: input.title,
      is_private: input.is_private ?? false,
      agenda: input.agenda ?? [],
      templates: input.templates ?? [],
    }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(res.status, body.error || `Failed to create meeting (HTTP ${res.status})`);
  }
  return (await res.json()) as { ok: boolean; meeting_id: string; slug: string; title: string };
}

/** Start a new occurrence of a meeting ("Start this week's <title>") — owner or member. Returns
 *  the new occurrence Topic (navigate to its `.slug`'s room). */
export async function startOccurrence(meetingId: string | number): Promise<Topic> {
  const res = await fetch(`/api/meetings/${encodeURIComponent(String(meetingId))}/occurrences`, {
    method: "POST",
    headers: { "content-type": "application/json" },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `Failed to start occurrence (HTTP ${res.status})`);
  }
  return (await res.json()) as Topic;
}

export async function fetchMeetingMembers(
  idOrSlug: string | number,
  signal?: AbortSignal,
): Promise<{ members: MeetingMember[]; my_role: MeetingMember["role"] | null; can_privatize: boolean }> {
  const res = await fetch(`/api/meetings/${encodeURIComponent(String(idOrSlug))}/members`, {
    signal,
    headers: { accept: "application/json" },
  });
  if (!res.ok) throw new Error(`Failed to load members (HTTP ${res.status})`);
  return (await res.json()) as {
    members: MeetingMember[];
    my_role: MeetingMember["role"] | null;
    can_privatize: boolean;
  };
}

/** Add (or update the role of) a meeting member — owner only, @codetogether.com only. */
export async function addMeetingMember(
  idOrSlug: string | number,
  email: string,
  role?: "owner" | "member",
): Promise<MeetingMember> {
  const res = await fetch(`/api/meetings/${encodeURIComponent(String(idOrSlug))}/members`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email, role }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `Failed to add member (HTTP ${res.status})`);
  }
  return (await res.json()) as MeetingMember;
}

/** Remove a meeting member — owner only. */
export async function removeMeetingMember(
  idOrSlug: string | number,
  email: string,
): Promise<void> {
  const res = await fetch(
    `/api/meetings/${encodeURIComponent(String(idOrSlug))}/members/${encodeURIComponent(email)}`,
    { method: "DELETE" },
  );
  if (!res.ok && res.status !== 404)
    throw new Error(`Failed to remove member (HTTP ${res.status})`);
}

/** Flip a meeting's privacy — owner (or, for a still-memberless public meeting, its creator). */
export async function setMeetingPrivacy(
  idOrSlug: string | number,
  isPrivate: boolean,
): Promise<{ is_private: boolean }> {
  const res = await fetch(`/api/meetings/${encodeURIComponent(String(idOrSlug))}/privacy`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ is_private: isPrivate }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `Failed to update privacy (HTTP ${res.status})`);
  }
  return (await res.json()) as { is_private: boolean };
}

export interface MeetingSchedule {
  recurrence: Recurrence | null;
  auto_create: boolean;
  next_run_at: string | null;
  last_occurrence_at: string | null;
}

/** Set (or update) a meeting's recurrence + auto-create schedule — owner only. 400 on a bad
 *  rule/tz (see `validateRecurrence`, server/src/meetings/recurrence.js). */
export async function setMeetingSchedule(
  idOrSlug: string | number,
  input: { recurrence: Recurrence; auto_create: boolean },
): Promise<MeetingSchedule> {
  const res = await fetch(`/api/meetings/${encodeURIComponent(String(idOrSlug))}/schedule`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ recurrence: input.recurrence, auto_create: input.auto_create }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `Failed to save schedule (HTTP ${res.status})`);
  }
  return (await res.json()) as MeetingSchedule;
}

/** Turn off a meeting's schedule — owner only. Clears recurrence/auto_create/next_run_at. */
export async function clearMeetingSchedule(idOrSlug: string | number): Promise<{ ok: boolean }> {
  const res = await fetch(`/api/meetings/${encodeURIComponent(String(idOrSlug))}/schedule`, {
    method: "DELETE",
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `Failed to turn off schedule (HTTP ${res.status})`);
  }
  return (await res.json()) as { ok: boolean };
}

// ── Calendar export (Phase D) ───────────────────────────────────────────────
// Two delivery paths for a meeting's ICS: a member-only authenticated one-time download, and a
// subscribable capability-feed URL Google/Outlook can poll with no session cookie. See SPEC.md
// §"Phase D — Calendar export". The feed URL is a bearer secret: shown once by `subscribe`,
// never re-fetched, never logged/persisted beyond the dialog's own React state.

/** The authenticated download href for a meeting's `.ics` — same-origin, so a plain
 *  `<a download href=...>` (or a programmatic navigation) carries the session cookie. */
export function meetingIcsUrl(idOrSlug: string | number): string {
  return `/api/meetings/${encodeURIComponent(String(idOrSlug))}/calendar.ics`;
}

/** Mint (or re-mint) my subscribable calendar-feed URL for this meeting — member-only. The
 *  plaintext `feed_url` is returned exactly once; only its hash is persisted server-side. */
export async function subscribeMeetingCalendar(
  idOrSlug: string | number,
): Promise<{ feed_url: string }> {
  const res = await fetch(
    `/api/meetings/${encodeURIComponent(String(idOrSlug))}/calendar/subscribe`,
    { method: "POST", headers: { "content-type": "application/json" } },
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `Failed to subscribe (HTTP ${res.status})`);
  }
  return (await res.json()) as { feed_url: string };
}

/** Revoke my calendar-feed token(s) for this meeting — member-only. The feed URL stops working
 *  immediately (the capability route re-checks membership/revocation on every fetch). */
export async function unsubscribeMeetingCalendar(
  idOrSlug: string | number,
): Promise<{ ok: boolean }> {
  const res = await fetch(
    `/api/meetings/${encodeURIComponent(String(idOrSlug))}/calendar/subscribe`,
    { method: "DELETE" },
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `Failed to unsubscribe (HTTP ${res.status})`);
  }
  return (await res.json()) as { ok: boolean };
}
